import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import UpgradePlanButton from '@features/Paywall/UpgradePlanButton';
import ContactSalesButton from '@features/Paywall/ContactSalesButton';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import Tooltip from '@components/Tooltip';
import { ModalConfirm } from '@components/Modal';
import { useTranslation, useClickSendSurveys } from '@hooks';
import { accountActions, alertActions, labelsActions, surveysActions, locationsProfilesActions } from '@actions';
import { surveyService } from '@services';
import { handleApiError } from '@helpers/api';
import { VIEW_TYPE } from '@constants/surveys';
import { BILLING_TYPE_MAP } from '@constants/billing';
import usePermissions from '@hooks/usePermissions';
import { SurveysIcon, SendIcon } from 'assets/images/icons';
import PageContainer from '@components/Layout/PageContainer';
import TemplateItem from './TemplateItem';
import EditModal from './EditModal';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';

const CardsGrid = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    '@media (max-width: 480px)': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
});

const SurveysPage = () => {
    const { t } = useTranslation();

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.EDIT });

    const account = useSelector(state => state.account?.account);
    const data = useSelector(state => state.surveys.data);
    const brands = useSelector(state => state.brands.data);
    const surveysTotalCount = useSelector(state => state.surveys.totalCount);
    const dispatch = useDispatch();

    const { hasFullAccess, hasFullSettings } = usePermissions();
    const { surveyExists, onClickSendSurveys } = useClickSendSurveys();
    const isUser = !hasFullAccess('surveys');
    const canUpgrade = hasFullSettings();
    const isQrOnlyPlan = account?.surveysEnabled === false;

    useEffect(() => {
        dispatch(surveysActions.getAll());
        dispatch(labelsActions.get());
    }, []);

    const onCloseEditModal = () => {
        // INFO: reset locations profiles to prevent trigger useEffect on availablePlatforms
        dispatch(locationsProfilesActions.reset());
        setModal({ open: false, type: VIEW_TYPE.EDIT });
        setSelectedSurvey(null);
    };

    const onCancelCreate = async () => {
        if (!selectedSurvey?.id) {
            onCloseEditModal();
            return;
        }
        try {
            await surveyService.deleteSurvey(selectedSurvey.id);
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error?.count });
        } finally {
            onCloseEditModal();
        }
    };

    const onCreateEmptySurvey = async () => {
        setIsSubmitting(true);
        try {
            const newSurvey = await surveyService.createEmptySurvey();
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            setSelectedSurvey(newSurvey);
            setModal({ open: true, type: VIEW_TYPE.CREATE });
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onCopySurvey = async survey => {
        setIsSubmitting(true);
        try {
            const copiedData = {
                ...survey,
                id: '00000000-0000-0000-0000-000000000000',
                name: `${survey.name} (${t('common.copy')})`,
            };
            delete copiedData.externalId;
            delete copiedData.createdAt;
            if (isQrOnlyPlan) {
                copiedData.satisfactionSurveyEnabled = false;
                copiedData.aiReviewSuggestionsEnabled = false;
                copiedData.sendSurveyEnabled = false;
                copiedData.followUpEnable = false;
                copiedData.questions = {};
                copiedData.pageCount = 1;
            }
            await surveyService.editSurvey(copiedData);
            PostHogManager.capture(ProductEvent.SurveyCreated, { source: 'copy' });
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.createSuccess')));
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await surveyService.deleteSurvey(selectedSurvey.id);
            await dispatch(surveysActions.getAll());
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setDeleteModalOpen(false);
            setSelectedSurvey(null);
            if (modal.open) {
                setModal({ open: false, type: VIEW_TYPE.EDIT });
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
    };

    return (
        <PageContainer>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<SurveysIcon />}
                title={t(isQrOnlyPlan ? 'FeedbackSurveys.titleQrOnly' : 'FeedbackSurveys.title')}
                subtitle={t(isQrOnlyPlan ? 'FeedbackSurveys.subtitleQrOnly' : 'FeedbackSurveys.subtitle')}
                metrics={
                    !isUser && (
                        <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                            {t(isQrOnlyPlan ? 'FeedbackSurveys.createdQrOnly' : 'FeedbackSurveys.created')}{' '}
                            {surveysTotalCount}
                        </Typography>
                    )
                }
            >
                {!isUser && (
                    <Button variant="contained" onClick={onCreateEmptySurvey} disabled={isSubmitting}>
                        {t(isQrOnlyPlan ? 'buttons.createQrCode' : 'buttons.addSurvey')}
                    </Button>
                )}
                {isQrOnlyPlan ? (
                    <>
                        {canUpgrade && (
                            <UpgradePlanButton variant="outlined" defaultPlan={BILLING_TYPE_MAP.PROFESSIONAL} />
                        )}
                        <ContactSalesButton variant="outlined" />
                    </>
                ) : (
                    <Tooltip title={surveyExists ? '' : t('FeedbackSurveys.sendableSurveyRequired')} placement="top">
                        <span>
                            <Button
                                variant="outlined"
                                onClick={onClickSendSurveys}
                                disabled={!surveyExists}
                                startIcon={<SendIcon />}
                                sx={{
                                    '& .MuiButton-startIcon svg': { width: 14, height: 14 },
                                }}
                            >
                                {t('buttons.sendSurveys')}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </PageHeader>
            <CardsGrid>
                {data.map(item => (
                    <TemplateItem
                        key={item.survey.id}
                        data={item}
                        companies={account?.companies}
                        brands={brands}
                        isSubmitting={isSubmitting}
                        isQrOnlyPlan={isQrOnlyPlan}
                        onClickCopy={() => onCopySurvey(item.survey)}
                        onClickEdit={() => {
                            setSelectedSurvey(item.survey);
                            setModal({ open: true, type: VIEW_TYPE.EDIT });
                        }}
                        onClickView={() => {
                            setSelectedSurvey(item.survey);
                            setModal({ open: true, type: VIEW_TYPE.VIEW });
                        }}
                        onClickDelete={() => {
                            setSelectedSurvey(item.survey);
                            setDeleteModalOpen(true);
                        }}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    onClose={onCloseEditModal}
                    onCancel={onCancelCreate}
                    account={account}
                    selectedSurvey={selectedSurvey}
                    setSelectedSurvey={setSelectedSurvey}
                    onDelete={() => setDeleteModalOpen(true)}
                    brands={brands}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </PageContainer>
    );
};

export default SurveysPage;
